import dayjs, { Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import { GroupOptions, TimePresetMap } from '../../../app/constants/time-selection-form';
import { crFont11 } from '../../../app/global-styles';
import { TimeRangeUtil } from '../../../app/utils/time';
import { useTenants } from '../../hooks/tenants';

// Import modal styles from the timeframe picker
import { Button, Icon, TimeframePicker, Tooltip } from '@controlrooms/components';
import {
  FooterButtons,
  ModalCloseButton,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@controlrooms/components/src/date-picker-v2/styles';
import { ICONS } from '@controlrooms/constants';
import { TimePresetKeyType, TimePresetKeys, TimeRanges, TimeSelection } from '@controlrooms/models';
import { TimeUtils } from '@controlrooms/utils';

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);

// Draft state interface for local timezone representation
interface DraftState {
  startDateTime: Dayjs; // Always in selected timezone
  endDateTime: Dayjs; // Always in selected timezone
  timezone: string; // Current timezone
  timeRange: TimeRanges; // PRESET or CUSTOM
  timePreset?: TimePresetKeyType;
}

// Timezone conversion utilities
const TimezoneUtils = {
  /**
   * Convert UTC TimeSelection to local draft state
   */
  convertUTCToLocal(utcTimeSelection: TimeSelection, targetTimezone: string): DraftState {
    const startDateTime = utcTimeSelection.startTime.tz(targetTimezone);
    const endDateTime = utcTimeSelection.endTime.tz(targetTimezone);

    return {
      startDateTime,
      endDateTime,
      timezone: targetTimezone,
      timeRange: utcTimeSelection.timeRange || TimeRanges.CUSTOM,
      timePreset: utcTimeSelection.timePreset,
    };
  },

  /**
   * Convert local draft state to UTC TimeSelection
   */
  convertLocalToUTC(draft: DraftState): TimeSelection {
    const startTimeUTC = draft.startDateTime.utc();
    const endTimeUTC = draft.endDateTime.utc();

    return new TimeSelection(
      startTimeUTC,
      endTimeUTC,
      draft.timezone,
      draft.timeRange === TimeRanges.PRESET ? endTimeUTC.diff(startTimeUTC, 'second') : 0,
      draft.timeRange || TimeRanges.CUSTOM,
      draft.timePreset,
    );
  },

  /**
   * Handle DST transitions by auto-correcting invalid times
   */
  handleDSTTransition(dateTime: Dayjs): Dayjs {
    // Check if the time is valid in the timezone
    const isValid = dateTime.isValid();

    if (isValid) {
      return dateTime;
    }

    // If invalid (DST transition), move to next valid time
    // For spring forward (2:00 -> 3:00), move to 3:00
    // For fall back (2:00 -> 1:00), use the second occurrence
    const hour = dateTime.hour();
    const minute = dateTime.minute();
    const second = dateTime.second();

    // For spring forward (2:00 AM -> 3:00 AM), move to 3:00 AM
    if (hour === 2) {
      return dateTime.hour(3).minute(0).second(0);
    }

    // Try to create a valid time
    let correctedTime = dateTime.hour(hour).minute(minute).second(second);

    // If still invalid, increment hour
    if (!correctedTime.isValid()) {
      correctedTime = dateTime.add(1, 'hour').startOf('hour');
    }

    return correctedTime;
  },

  /**
   * Validate and correct time string format
   */
  validateAndCorrectTime(time: string): string {
    const parts = time.split(':');
    const hours = Number(parts[0]) || 0;
    const minutes = Number(parts[1]) || 0;
    const seconds = Number(parts[2]) || 0;

    // Handle invalid input
    if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) {
      return '00:00:00';
    }

    let correctedHours = hours || 0;
    let correctedMinutes = minutes || 0;
    let correctedSeconds = seconds || 0;

    // Handle overflow cases with rollover logic
    if (correctedSeconds > 59) {
      correctedMinutes += Math.floor(correctedSeconds / 60);
      correctedSeconds = correctedSeconds % 60;
    }

    if (correctedMinutes > 59) {
      correctedHours += Math.floor(correctedMinutes / 60);
      correctedMinutes = correctedMinutes % 60;
    }

    if (correctedHours > 23) {
      correctedHours = 23;
      correctedMinutes = 59;
      correctedSeconds = 59;
    }

    // Final validation - ensure all values are within valid ranges
    correctedHours = Math.min(23, correctedHours);
    correctedMinutes = Math.min(59, correctedMinutes);
    correctedSeconds = Math.min(59, correctedSeconds);

    // Clamp negative values
    correctedHours = Math.max(0, correctedHours);
    correctedMinutes = Math.max(0, correctedMinutes);
    correctedSeconds = Math.max(0, correctedSeconds);

    return `${correctedHours.toString().padStart(2, '0')}:${correctedMinutes
      .toString()
      .padStart(2, '0')}:${correctedSeconds.toString().padStart(2, '0')}`;
  },

  /**
   * Change timezone while preserving local date/time values
   */
  changeTimezone(draft: DraftState, newTimezone: string): DraftState {
    // Extract local date/time components
    const startYear = draft.startDateTime.year();
    const startMonth = draft.startDateTime.month();
    const startDate = draft.startDateTime.date();
    const startHour = draft.startDateTime.hour();
    const startMinute = draft.startDateTime.minute();
    const startSecond = draft.startDateTime.second();

    const endYear = draft.endDateTime.year();
    const endMonth = draft.endDateTime.month();
    const endDate = draft.endDateTime.date();
    const endHour = draft.endDateTime.hour();
    const endMinute = draft.endDateTime.minute();
    const endSecond = draft.endDateTime.second();

    // Create new times with same local values but in new timezone
    const newStartDateTime = dayjs.tz(
      `${startYear}-${(startMonth + 1).toString().padStart(2, '0')}-${startDate
        .toString()
        .padStart(2, '0')} ${startHour.toString().padStart(2, '0')}:${startMinute
        .toString()
        .padStart(2, '0')}:${startSecond.toString().padStart(2, '0')}`,
      newTimezone,
    );

    const newEndDateTime = dayjs.tz(
      `${endYear}-${(endMonth + 1).toString().padStart(2, '0')}-${endDate
        .toString()
        .padStart(2, '0')} ${endHour.toString().padStart(2, '0')}:${endMinute
        .toString()
        .padStart(2, '0')}:${endSecond.toString().padStart(2, '0')}`,
      newTimezone,
    );

    // Handle DST transitions
    const correctedStartDateTime = TimezoneUtils.handleDSTTransition(newStartDateTime);
    const correctedEndDateTime = TimezoneUtils.handleDSTTransition(newEndDateTime);

    return {
      ...draft,
      startDateTime: correctedStartDateTime,
      endDateTime: correctedEndDateTime,
      timezone: newTimezone,
    };
  },

  /**
   * Apply preset selection relative to current timezone
   */
  applyPreset(
    draft: DraftState,
    preset: { relative?: { seconds: number }; key: TimePresetKeyType },
  ): DraftState {
    if (!preset.relative) {
      return draft;
    }

    // Calculate relative times from now in the current timezone
    const now = dayjs().tz(draft.timezone);
    const startDateTime = now.subtract(preset.relative.seconds, 'seconds');
    const endDateTime = now;

    // Handle DST transitions
    const correctedStartDateTime = TimezoneUtils.handleDSTTransition(startDateTime);
    const correctedEndDateTime = TimezoneUtils.handleDSTTransition(endDateTime);

    return {
      startDateTime: correctedStartDateTime,
      endDateTime: correctedEndDateTime,
      timezone: draft.timezone,
      timeRange: TimeRanges.PRESET,
      timePreset: preset.key,
    };
  },
};

const Container = styled.div`
  display: flex;
  width: 100%;
  min-width: 0; /* Allow flex shrinking */
  cursor: pointer;
  overflow: visible; /* Allow tooltip to be visible */
  &:hover .primary-label {
    color: ${({ theme }) => theme.v2.timeDisplay.primaryTextHover};
    * {
      color: ${({ theme }) => theme.v2.timeDisplay.primaryTextHover};
    }
  }
`;

const TimeContainer = styled.div`
  display: flex;
  align-items: center;
  text-align: center;
  min-width: 20px;

  svg {
    flex-shrink: 0;
  }
`;

const TimeCard = styled.div`
  display: flex;
  flex-direction: row;
  padding: 0;
  gap: 10px;
  overflow: visible;
  min-width: 0;
  flex: 1;
`;

const Label = styled.div`
  font-style: normal;
  font-weight: 400;
`;

const PrimaryLabel = styled(Label)`
  ${crFont11}
  margin-right: 7px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  flex: 1;
  display: block;
  color: ${({ theme }) => theme.v2.timeDisplay.primaryTextColor};

  .date {
    font-weight: 300;
    color: ${({ theme }) => theme.v2.timeDisplay.secondaryTextColor};
  }
`;

const TooltipWrapper = styled.div`
  display: flex;
  min-width: 0;
  flex: 1;

  & > span {
    display: flex;
    min-width: 0;
    flex: 1;
  }
`;

interface TimeRangePickerProps {
  value: TimeSelection;
  onApply: (timeSelection: TimeSelection, updateHistory?: boolean) => void;
  onCancel?: () => void;
  title?: string;
  showDuration?: boolean;
  showTimezone?: boolean;
  allowTimezoneChange?: boolean;
  tooltipPosition?: 'up' | 'down' | 'left' | 'right';
}

const AppTimeRangePicker = ({
  value,
  onApply,
  onCancel,
  title = 'Select Timeframe',
  showDuration = true,
  showTimezone = false,
  allowTimezoneChange = true,
  tooltipPosition = 'up',
}: TimeRangePickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState<TimeSelection | null>(null);
  // Separate visual state for live clock display (like _startTime in time-display.tsx)
  const [visualTimeSelection, setVisualTimeSelection] = useState<TimeSelection | null>(null);
  const { currentTenant } = useTenants();
  const tenantTimezone = currentTenant?.timezone || dayjs.tz.guess();

  // Get current time selection from props
  const { streamingTimeInSeconds, nowSelected } = value;

  // Initialize draft state from UTC TimeSelection
  useEffect(() => {
    if (value) {
      // Store UTC TimeSelection directly - no conversion needed
      setDraft(value);
    }
  }, [value]);

  // Convert existing presets to TimePreset format using the same structure as selectMapper
  const presets = useMemo(
    () => [
      ...GroupOptions.flatMap((groupOption) =>
        groupOption.options.map((option) => ({
          key: option.value as TimePresetKeyType,
          label: option.label,
          relative: {
            seconds: (() => {
              switch (option.value) {
                case TimePresetKeys.LAST_HOUR:
                  return 60 * 60;
                case TimePresetKeys.LAST_FOUR_HOURS:
                  return 4 * 60 * 60;
                case TimePresetKeys.LAST_TWELVE_HOURS:
                  return 12 * 60 * 60;
                case TimePresetKeys.LAST_DAY:
                  return 24 * 60 * 60;
                case TimePresetKeys.LAST_WEEK:
                  return 7 * 24 * 60 * 60;
                case TimePresetKeys.LAST_THIRTY_DAYS:
                  return 30 * 24 * 60 * 60;
                case TimePresetKeys.SIX_MONTHS:
                  return 6 * 30 * 24 * 60 * 60; // 6 months
                case TimePresetKeys.LAST_YEAR:
                  return 365 * 24 * 60 * 60; // 1 year
                default:
                  return 24 * 60 * 60; // Default to 1 day
              }
            })(),
          },
          keywords: [option.label.toLowerCase()],
        })),
      ),
    ],
    [],
  );

  // Validation logic
  const currentDraft = draft || value;
  const isValidSelection = currentDraft?.endTime.isAfter(currentDraft?.startTime) || false;
  const isWithinBounds = true; // Add your bounds logic here if needed

  // Memoize the timeSelection to prevent infinite re-renders
  const memoizedTimeSelection = useMemo(() => {
    if (!draft) return value;
    return draft; // draft is already UTC TimeSelection
  }, [draft, value]);

  // Memoize the onDraftChange callback to prevent infinite re-renders
  const memoizedOnDraftChange = useCallback((newDraft: TimeSelection | null) => {
    if (newDraft) {
      // Store UTC TimeSelection directly - no conversion needed
      setDraft(newDraft);
    }
  }, []);

  // Handle modal close functionality
  const handleModalClose = useCallback(() => {
    setIsOpen(false);
    setDraft(null); // Reset draft when closing
    onCancel?.(); // Call onCancel callback if provided
  }, [onCancel]);

  // Handle escape key press
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        handleModalClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isOpen, handleModalClose]);

  // Live clock logic for presets (matches time-display.tsx pattern)
  useEffect(() => {
    if (streamingTimeInSeconds) {
      // Visual timer - updates display every second (like _setStartTime in time-display.tsx)
      let timer: ReturnType<typeof setTimeout>;
      const tick = (waitMs: number) => {
        timer = setTimeout(() => {
          // Only update visual state for display - don't call onApply
          if (draft) {
            const now = dayjs();
            // When nowSelected is true, keep original startTime fixed, only update endTime
            const newStartTime = draft.nowSelected
              ? draft.startTime
              : now.subtract(streamingTimeInSeconds, 'seconds');
            const updatedVisualTimeSelection = new TimeSelection(
              newStartTime,
              now,
              value.timezone,
              streamingTimeInSeconds,
              value.timeRange,
              value.timePreset,
            );
            updatedVisualTimeSelection.nowSelected = draft.nowSelected;
            setVisualTimeSelection(updatedVisualTimeSelection);
          }
          tick(1000 - (Date.now() % 1000)); // set up the next tick
        }, waitMs);
      };
      tick(1000 - (Date.now() % 1000));

      // State timer - updates parent state every 30s/5min (like setViewState in time-display.tsx)
      const timeContextRefresh =
        streamingTimeInSeconds <= dayjs.duration(4, 'd').asSeconds() ? 30000 : 300000;
      const timeContextId = setInterval(() => {
        const now = dayjs();
        // When nowSelected is true, keep original startTime fixed, only update endTime
        const newStartTime = value.nowSelected
          ? value.startTime
          : now.subtract(streamingTimeInSeconds as number, 'seconds');

        // Apply live time updates to parent (actual state update)
        const updatedTimeSelection = new TimeSelection(
          newStartTime,
          now,
          value.timezone,
          streamingTimeInSeconds,
          value.timeRange,
          value.timePreset,
        );
        updatedTimeSelection.nowSelected = value.nowSelected;
        onApply(updatedTimeSelection, false);
      }, timeContextRefresh);

      return () => {
        timer && clearTimeout(timer);
        clearInterval(timeContextId);
      };
    }
  }, [streamingTimeInSeconds, value, onApply, draft]);

  // Initialize visual state when value changes
  useEffect(() => {
    setVisualTimeSelection(value);
  }, [value]);

  // Methods from time-display.tsx
  const preparePrimaryLabel = () => {
    // Use display value instead of drafted time
    const displayValue = visualTimeSelection || value || memoizedTimeSelection;
    if (!displayValue) return '';

    const hoursDisplay = TimeRangeUtil.calculateTimePreset('label', streamingTimeInSeconds);
    const startDateTime = dayjs.utc(displayValue.startTime).tz(displayValue.timezone);
    const endDateTime = dayjs.utc(displayValue.endTime).tz(displayValue.timezone);

    if (streamingTimeInSeconds) {
      // For presets: show only preset name
      if (displayValue.nowSelected) {
        return `${startDateTime.format('MMM DD `YY HH:mm:ss')} ${TimeUtils.zoneFormat(
          displayValue.timezone,
          'z',
        )} - Now`;
      }
      return hoursDisplay || '';
    } else {
      // For custom ranges: {start date time tz - end date time tz}

      return `${startDateTime.format('MMM DD `YY HH:mm:ss')} ${TimeUtils.zoneFormat(
        displayValue.timezone,
        'z',
      )} - ${endDateTime.format('MMM DD `YY HH:mm:ss')} ${TimeUtils.zoneFormat(
        displayValue.timezone,
        'z',
      )}`;
    }
  };

  // change the calculation to account for daylight saving times in certain date selections
  const calculateForDST = () => {
    // Use display value instead of drafted time
    const displayValue = visualTimeSelection || value || memoizedTimeSelection;
    if (!displayValue) {
      return { seconds: 0, minutes: 0, hours: 0, days: 0 };
    }

    const startTime = dayjs.utc(displayValue.startTime);
    const endTime = dayjs.utc(displayValue.endTime);

    const startTimeUtcOffset = startTime.utcOffset();
    const endTimeUtcOffset = endTime.utcOffset();
    const dstAdjustment = (endTimeUtcOffset - startTimeUtcOffset) / 60;

    const totalHours = endTime.diff(startTime, 'hours') + dstAdjustment;
    return {
      seconds: Math.floor(endTime.diff(startTime, 's') % 60),
      minutes: Math.floor(endTime.diff(startTime, 'm') % 60),
      hours: totalHours % 24,
      days: Math.floor(totalHours / 24),
    };
  };

  const getTimeDiff = calculateForDST();

  const getDurationContent = () => {
    // Use display value instead of drafted time
    const displayValue = visualTimeSelection || value || memoizedTimeSelection;
    if (!displayValue) return '';

    const startTime = dayjs.utc(displayValue.startTime);
    const endTime = dayjs.utc(displayValue.endTime);

    const parts = [];
    if (getTimeDiff.days > 0) parts.push(`${getTimeDiff.days}d`);
    if (getTimeDiff.hours > 0) parts.push(`${getTimeDiff.hours}h`);
    if (getTimeDiff.minutes > 0) parts.push(`${getTimeDiff.minutes}m`);
    if (endTime.diff(startTime, 'm') < 1 && getTimeDiff.seconds > 0) {
      parts.push(`${getTimeDiff.seconds}s`);
    }
    return parts.join(' ');
  };

  const getPrimaryLabelContent = () => {
    // Use visual state for live clock, otherwise use applied value
    const displayValue = visualTimeSelection || value || memoizedTimeSelection;
    if (!displayValue) return '';

    // Simply convert UTC times to the selected timezone and format
    const convertedTimeDisplay = `${dayjs
      .utc(displayValue.startTime)
      .tz(displayValue.timezone)
      .format('MMM DD `YY HH:mm:ss')} ${TimeUtils.zoneFormat(displayValue.timezone, 'z')} - ${dayjs
      .utc(displayValue.endTime)
      .tz(displayValue.timezone)
      .format('MMM DD `YY HH:mm:ss')} ${TimeUtils.zoneFormat(displayValue.timezone, 'z')}`;

    return convertedTimeDisplay;
  };

  const getSubLabelContent = () => {
    // Use visual state for live clock, otherwise use applied value
    const displayValue = visualTimeSelection || value || memoizedTimeSelection;
    if (!displayValue) return '';

    if (streamingTimeInSeconds) {
      // For presets: show full date range
      const startDateTime = dayjs.utc(displayValue.startTime).tz(displayValue.timezone);
      const endDateTime = dayjs.utc(displayValue.endTime).tz(displayValue.timezone);

      return `${startDateTime.format('MMM DD `YY HH:mm:ss')} ${TimeUtils.zoneFormat(
        displayValue.timezone,
        'z',
      )} - ${endDateTime.format('MMM DD `YY HH:mm:ss')} ${TimeUtils.zoneFormat(
        displayValue.timezone,
        'z',
      )}`;
    } else {
      // For custom ranges: show duration
      return getDurationContent();
    }
  };

  return (
    <>
      <Container onClick={() => setIsOpen(!isOpen)}>
        <TimeCard data-testid="timecard_table">
          <TimeContainer data-testid="time-container">
            <TooltipWrapper>
              <Tooltip
                version="v2"
                direction={tooltipPosition}
                label={
                  streamingTimeInSeconds
                    ? `${preparePrimaryLabel()}, ${
                        nowSelected ? getDurationContent() : getSubLabelContent()
                      }`
                    : `${getPrimaryLabelContent()}, ${getDurationContent()}`
                }
              >
                <PrimaryLabel data-testid="timecard_date" className="primary-label">
                  {streamingTimeInSeconds ? (
                    <>
                      {preparePrimaryLabel()},
                      {nowSelected ? <> {getDurationContent()}</> : <> {getSubLabelContent()}</>}
                    </>
                  ) : (
                    <>
                      {getPrimaryLabelContent()},<> {getDurationContent()}</>
                    </>
                  )}
                </PrimaryLabel>
              </Tooltip>
            </TooltipWrapper>
            <Icon name={ICONS.DateTimeCalendar} />
          </TimeContainer>
        </TimeCard>
      </Container>
      {isOpen &&
        createPortal(
          <ModalOverlay onClick={handleModalClose}>
            <ModalContainer onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>{title}</ModalTitle>
                <ModalCloseButton onClick={handleModalClose}>
                  <Icon name="close" />
                </ModalCloseButton>
              </ModalHeader>
              <TimeframePicker
                value={memoizedTimeSelection}
                presets={presets}
                timePresetMap={TimePresetMap}
                onDraftChange={memoizedOnDraftChange}
                mode="range"
                calendarLayout="dual"
                includeTime={showDuration}
                allowTimezoneChange={allowTimezoneChange}
                timeFormat="24h"
                plantTimezone={tenantTimezone}
                showTimezone={showTimezone}
              />
              <ModalFooter>
                <FooterButtons>
                  <Button buttonType="secondary" buttonSize="small" onClick={handleModalClose}>
                    Cancel
                  </Button>
                  <Button
                    buttonType="primary"
                    buttonSize="small"
                    onClick={() => {
                      if (draft) {
                        onApply(draft, true);
                        setIsOpen(false);
                      }
                    }}
                    disabled={!isValidSelection || !isWithinBounds}
                  >
                    OK
                  </Button>
                </FooterButtons>
              </ModalFooter>
            </ModalContainer>
          </ModalOverlay>,
          document.body,
        )}
    </>
  );
};

export default AppTimeRangePicker;
